<script setup>
import { onMounted, computed, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const commissions = useMapGetter('reseller/getCommissions');
const withdrawals = useMapGetter('reseller/getWithdrawals');

const tp = (key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const formatMoney = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const totalPending = computed(() => commissions.value?.total_pending || 0);
const totalPaid = computed(() => commissions.value?.total_paid || 0);
const entries = computed(() => commissions.value?.entries || []);

const available = computed(() => withdrawals.value?.available || 0);
const withdrawalList = computed(() => withdrawals.value?.withdrawals || []);

// Níveis de comissão (10/5/2) — mostra ao parceiro quanto ganha em cada nível.
const levels = computed(() => commissions.value?.levels || []);
const formatRate = rate => `${Number(rate || 0).toFixed(0)}%`;

// --- Saque ---
const MIN_WITHDRAWAL = 50;
const showWithdraw = ref(false);
const submitting = ref(false);
const form = ref({ amount: 0, pixKey: '' });

const belowMinimum = computed(() => Number(form.value.amount) < MIN_WITHDRAWAL);

const openWithdraw = () => {
  form.value = { amount: available.value, pixKey: '' };
  showWithdraw.value = true;
};

const submitWithdraw = async () => {
  const amount = Number(form.value.amount);
  if (
    !amount ||
    amount <= 0 ||
    amount > available.value ||
    amount < MIN_WITHDRAWAL
  )
    return;
  submitting.value = true;
  try {
    await store.dispatch('reseller/createWithdrawal', {
      amount,
      pixKey: form.value.pixKey,
    });
    useAlert(tp('REVENDA.COMMISSIONS.WITHDRAW.REQUESTED', 'Saque solicitado!'));
    showWithdraw.value = false;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        tp('REVENDA.COMMISSIONS.WITHDRAW.ERROR', 'Erro ao solicitar saque.')
    );
  } finally {
    submitting.value = false;
  }
};

const statusClass = status =>
  ({
    paid: 'bg-n-teal-3 text-n-teal-11',
    approved: 'bg-n-blue-3 text-n-blue-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
    rejected: 'bg-n-ruby-3 text-n-ruby-11',
    cancelled: 'bg-n-slate-3 text-n-slate-11',
  })[status] || 'bg-n-slate-3 text-n-slate-11';

const statusLabel = status =>
  ({
    paid: tp('REVENDA.COMMISSIONS.STATUSES.PAID', 'Pago'),
    approved: tp('REVENDA.COMMISSIONS.STATUSES.APPROVED', 'Aprovado'),
    pending: tp('REVENDA.COMMISSIONS.STATUSES.PENDING', 'Pendente'),
    rejected: tp('REVENDA.COMMISSIONS.STATUSES.REJECTED', 'Recusado'),
    cancelled: tp('REVENDA.COMMISSIONS.STATUSES.CANCELLED', 'Cancelado'),
  })[status] || status;

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

onMounted(() => {
  store.dispatch('reseller/getCommissions');
  store.dispatch('reseller/getWithdrawals');
});
</script>

<template>
  <div class="flex flex-col w-full gap-4 py-6">
    <div>
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('REVENDA.COMMISSIONS.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('REVENDA.COMMISSIONS.SUBTITLE') }}
      </p>
    </div>

    <div class="grid items-start gap-4 lg:grid-cols-2">
      <!-- Coluna esquerda: como funciona + saldo/saque + KPIs -->
      <div class="flex flex-col gap-4">
        <!-- Como funciona: níveis de comissão -->
        <div
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-network size-4 text-n-brand" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ tp('REVENDA.COMMISSIONS.HOW_TITLE', 'Como você ganha') }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11">
            {{
              tp(
                'REVENDA.COMMISSIONS.HOW_HINT',
                'Indique clientes pelo seu link. Você ganha sobre o que eles pagam todo mês — e também sobre indicações feitas pela sua rede, em até 3 níveis.'
              )
            }}
          </p>
          <div v-if="levels.length" class="grid grid-cols-3 gap-2">
            <div
              v-for="lvl in levels"
              :key="lvl.level"
              class="flex flex-col items-center gap-0.5 p-3 rounded-lg bg-n-background border border-n-weak"
            >
              <span class="text-xs text-n-slate-10">
                {{ tp('REVENDA.COMMISSIONS.LEVEL', 'Nível') }} {{ lvl.level }}
              </span>
              <span class="text-lg font-semibold text-n-brand tabular-nums">
                {{ formatRate(lvl.rate) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Saldo disponível + pedir saque -->
        <div
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-brand/40 bg-n-brand/5 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{
                tp(
                  'REVENDA.COMMISSIONS.AVAILABLE',
                  'Saldo disponível para saque'
                )
              }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ formatMoney(available) }}
            </span>
          </div>
          <Button
            icon="i-lucide-banknote-arrow-down"
            :disabled="available < MIN_WITHDRAWAL || showWithdraw"
            :label="tp('REVENDA.COMMISSIONS.WITHDRAW.BUTTON', 'Pedir saque')"
            @click="openWithdraw"
          />
        </div>

        <!-- Formulário de saque -->
        <div
          v-if="showWithdraw"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ tp('REVENDA.COMMISSIONS.WITHDRAW.AMOUNT', 'Valor do saque') }}
              <input
                v-model.number="form.amount"
                type="number"
                min="0"
                :max="available"
                step="0.01"
                class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{
                tp(
                  'REVENDA.COMMISSIONS.WITHDRAW.PIX_KEY',
                  'Chave PIX para receber'
                )
              }}
              <input
                v-model="form.pixKey"
                type="text"
                :placeholder="
                  tp(
                    'REVENDA.COMMISSIONS.WITHDRAW.PIX_PLACEHOLDER',
                    'CPF, e-mail, telefone ou chave aleatória'
                  )
                "
                class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
              />
            </label>
          </div>
          <p class="text-xs text-n-slate-10">
            {{
              tp(
                'REVENDA.COMMISSIONS.WITHDRAW.HINT',
                'O saque é analisado e pago por nós via PIX. Você recebe o valor solicitado do seu saldo de comissão.'
              )
            }}
          </p>
          <p v-if="belowMinimum" class="text-xs text-n-ruby-9">
            {{
              tp(
                'REVENDA.COMMISSIONS.WITHDRAW.MINIMUM',
                'Saque mínimo R$50 (taxas).'
              )
            }}
          </p>
          <div class="flex justify-end gap-2">
            <Button
              variant="clear"
              size="sm"
              :label="tp('REVENDA.COMMISSIONS.WITHDRAW.CANCEL', 'Cancelar')"
              @click="showWithdraw = false"
            />
            <Button
              color="teal"
              size="sm"
              :is-loading="submitting"
              :disabled="
                !form.amount ||
                form.amount <= 0 ||
                form.amount > available ||
                belowMinimum
              "
              :label="
                tp('REVENDA.COMMISSIONS.WITHDRAW.CONFIRM', 'Solicitar saque')
              "
              @click="submitWithdraw"
            />
          </div>
        </div>
      </div>

      <!-- Coluna direita: KPIs + histórico -->
      <div class="flex flex-col gap-4">
        <!-- Pendente + Pago (largura toda, metade cada) -->
        <div class="grid grid-cols-2 gap-3">
          <div
            class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="text-base i-lucide-clock" />
              <span class="text-xs">{{
                t('REVENDA.COMMISSIONS.TOTAL_PENDING')
              }}</span>
            </div>
            <span class="text-xl font-semibold text-n-slate-12 tabular-nums">
              {{ formatMoney(totalPending) }}
            </span>
          </div>
          <div
            class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="text-base i-lucide-circle-check-big" />
              <span class="text-xs">{{
                t('REVENDA.COMMISSIONS.TOTAL_PAID')
              }}</span>
            </div>
            <span class="text-xl font-semibold text-n-slate-12 tabular-nums">
              {{ formatMoney(totalPaid) }}
            </span>
          </div>
        </div>

        <!-- Meus saques -->
        <div v-if="withdrawalList.length" class="flex flex-col gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ tp('REVENDA.COMMISSIONS.WITHDRAW.HISTORY', 'Meus saques') }}
          </h3>
          <div class="overflow-x-auto border rounded-xl border-n-weak">
            <table class="w-full min-w-full text-sm text-left">
              <thead class="text-n-slate-11 bg-n-solid-2">
                <tr>
                  <th class="px-4 py-2 font-medium">
                    {{ tp('REVENDA.COMMISSIONS.WITHDRAW.DATE', 'Data') }}
                  </th>
                  <th class="px-4 py-2 font-medium">
                    {{ t('REVENDA.COMMISSIONS.AMOUNT') }}
                  </th>
                  <th class="px-4 py-2 font-medium">
                    {{
                      tp('REVENDA.COMMISSIONS.WITHDRAW.PIX_KEY', 'Chave PIX')
                    }}
                  </th>
                  <th class="px-4 py-2 font-medium">
                    {{ t('REVENDA.COMMISSIONS.STATUS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="w in withdrawalList" :key="w.id">
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ formatDate(w.requested_at || w.created_at) }}
                  </td>
                  <td
                    class="px-4 py-3 font-medium text-n-slate-12 tabular-nums"
                  >
                    {{ formatMoney(w.amount) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11 truncate max-w-[12rem]">
                    {{ w.pix_key || '—' }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="inline-flex items-center px-2 py-0.5 text-xs rounded-full"
                      :class="statusClass(w.status)"
                    >
                      {{ statusLabel(w.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-if="entries.length"
          class="overflow-x-auto border rounded-xl border-n-weak"
        >
          <table class="w-full min-w-full text-sm text-left">
            <thead class="text-n-slate-11 bg-n-solid-2">
              <tr>
                <th class="px-4 py-2 font-medium">
                  {{ t('REVENDA.COMMISSIONS.LEVEL') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('REVENDA.COMMISSIONS.SOURCE') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('REVENDA.COMMISSIONS.SOURCE_AMOUNT') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('REVENDA.COMMISSIONS.AMOUNT') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('REVENDA.COMMISSIONS.DATE') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('REVENDA.COMMISSIONS.STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="entry in entries" :key="entry.id">
                <td class="px-4 py-3 text-n-slate-11">
                  <button
                    v-tooltip.bottom="entry.beneficiary"
                    :title="entry.beneficiary"
                    class="inline-flex items-center gap-1"
                  >
                    {{
                      t('REVENDA.COMMISSIONS.LEVEL_N', { level: entry.level })
                    }}
                    <span class="i-lucide-info" />
                  </button>
                </td>
                <td class="px-4 py-3 text-n-slate-12">
                  {{ entry.source_account || '—' }}
                </td>
                <td class="px-4 py-3 text-n-slate-11 tabular-nums">
                  {{ formatMoney(entry.base_amount) }}
                </td>
                <td class="px-4 py-3 font-medium text-n-slate-12 tabular-nums">
                  {{ formatMoney(entry.amount) }}
                  <span class="text-n-slate-10">({{ entry.rate }}%)</span>
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  {{ formatDate(entry.created_at) }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center px-2 py-0.5 text-xs rounded-full"
                    :class="statusClass(entry.status)"
                  >
                    {{ statusLabel(entry.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="flex flex-col items-center gap-2 p-8 text-center border rounded-xl border-n-weak"
        >
          <span class="text-2xl i-lucide-coins text-n-slate-10" />
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('REVENDA.COMMISSIONS.EMPTY') }}
          </p>
          <p class="max-w-sm text-xs text-n-slate-10">
            {{
              tp(
                'REVENDA.COMMISSIONS.EMPTY_HINT',
                'Compartilhe seu link de indicação (no topo da página) para trazer clientes e começar a ganhar comissão.'
              )
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
